/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react';
import { useCallback, useEffect, useState } from 'react';
import { Flex, Box } from 'rebass';
import { arrayOf, bool, func, string } from 'prop-types';
import { connect } from 'react-redux';
import {
  ButtonStyled,
  CheckboxSetStyled,
  CheckboxStyled,
  DropDownStyled,
  Grid,
  GridItem,
  TextFieldStyled,
  TextStyled
} from '@nike/nike-design-system-components';
import { useFormikContext } from 'formik';

// Components
import DatePicker from '../../common/DatePicker';
import CatalogTypeField from '../../catalogTypes/CatalogTypeField';
import { StepBox } from '../../../styles/styles';

// Constants
import { ORDER_TYPES, ORDER_TYPE_NAMES, SALES_ORGS } from '../constants';
import OptionRangeSelect from '../../../components/formInputs/OptionRangeSelect';
import { getSeasons } from '../selectors';
import { seasonType } from '../propTypes';
import { fetchAdminUsers } from '../../../admin/user/actions';
import { isDirectShip, MandatoryFields } from '../utils';
import { getUserData } from '../../../user/selectors';

const { ATONCE, FUTURES, FORECAST } = ORDER_TYPE_NAMES;

// Styles
const fadeIn = keyframes`from { opacity: 0 } to { opacity: 1 }`;

export const CatalogsCreateOrEditForm = ({ fetchAdminUsers, onCancel, seasons, userId, isEditing }) => {
  const { values, handleChange, touched, errors, setFieldValue, isSubmitting, handleSubmit, handleBlur } =
    useFormikContext();
  const doesOrderTypesInclude = orderType => values.orderTypes.includes(orderType);
  const disableCheckbox = doesOrderTypesInclude(FUTURES) || doesOrderTypesInclude(FORECAST);
  const seasonOptions = seasons.map(season => ({ label: season.name, value: season.name }));
  const [sysAdmins, setSysAdmins] = useState([]);

  const getFieldError = field => {
    return touched[field] && errors[field];
  };

  const nameError = getFieldError('name');
  const groupError = getFieldError('groupName');
  const salesOrgError = getFieldError('salesOrg');
  const catalogTypesError = getFieldError('catalogTypes');

  const orderTypesError = getFieldError('orderTypes');

  const atOnceStartError = getFieldError('atOnceStart');
  const atOnceEndError = getFieldError('atOnceEnd');

  const futuresCrdError = getFieldError('futuresCrd');
  const futuresStartError = getFieldError('futuresStart');
  const futuresEndError = getFieldError('futuresEnd');

  const forecastCrdError = getFieldError('forecastCrd');
  const forecastStartError = getFieldError('forecastStart');
  const forecastEndError = getFieldError('forecastEnd');

  const seasonsError = getFieldError('seasons');

  const orderTypesIncludesFuturesOrForecast =
    values.orderTypes.includes(FUTURES) || values.orderTypes.includes(FORECAST);

  const associateCatalogType = setFieldValue => catalogTypeId => values => {
    setFieldValue('catalogTypes', [...values.catalogTypes, catalogTypeId]);
  };

  const disassociateCatalogType = setFieldValue => id => values => {
    setFieldValue(
      'catalogTypes',
      values.catalogTypes.filter(typeId => typeId !== id)
    );
  };

  const handleSingleSeasonChange = useCallback(
    ({ target: { checked } }) => {
      setFieldValue('singleSeason', checked);

      if (checked) {
        setFieldValue('seasons', [values.seasons[0] || seasonOptions[0].value]);
      }
    },
    [setFieldValue, values.seasons, seasonOptions]
  );

  const handleSingleSeasonSelect = useCallback(
    ({ target: { value } }) => {
      setFieldValue('seasons', [value]);
    },
    [setFieldValue]
  );

  const handleSeasonRangeSelect = useCallback(
    value => {
      setFieldValue('seasons', value);
    },
    [setFieldValue]
  );

  const handleOrderTypesChange = useCallback(
    ({ target: { id, checked } }) => {
      const newValues = checked ? [...values.orderTypes, id] : values.orderTypes.filter(type => type !== id);
      setFieldValue('orderTypes', newValues);

      if (!newValues.includes(ATONCE)) {
        setFieldValue('atOnceStart', '');
        setFieldValue('atOnceEnd', '');
      }

      if (!newValues.includes(FUTURES)) {
        setFieldValue('futuresCrd', '');
        setFieldValue('futuresStart', '');
        setFieldValue('futuresEnd', '');
      }

      if (!newValues.includes(FORECAST)) {
        setFieldValue('forecastCrd', '');
        setFieldValue('forecastStart', '');
        setFieldValue('forecastEnd', '');
      }

      if (newValues.includes(FORECAST) || newValues.includes(ATONCE)) {
        setFieldValue('directShip', false);
      }

      if (!newValues.includes(FORECAST) || !newValues.includes(ATONCE)) {
        setFieldValue('singleSeason', false);
      }

      if (newValues.includes(FUTURES) || newValues.includes(FORECAST)) {
        setFieldValue('singleSeason', true);
        if (values.seasons[0]) {
          setFieldValue('seasons', [values.seasons[0]]);
        }
      }
    },
    [setFieldValue, values.seasons, values.orderTypes]
  );

  const handleCancel = useCallback(
    event => {
      event.preventDefault();
      onCancel();
    },
    [onCancel]
  );

  useEffect(() => {
    const getSysAdmins = async () => {
      const data = await fetchAdminUsers({});
      setSysAdmins(data?.map(({ email, id }) => ({ label: email, value: id })));
    };

    getSysAdmins();
  }, []);

  useEffect(() => {
    if (!values.ownerId && userId) {
      setFieldValue('ownerId', userId);
    }
  }, [values.ownerId, userId]);

  useEffect(() => {
    if (orderTypesIncludesFuturesOrForecast) {
      setFieldValue('singleSeason', true);

      if (values.seasons[0]) {
        setFieldValue('seasons', [values.seasons[0]]);
      }
    }
  }, [orderTypesIncludesFuturesOrForecast, values.seasons[0]]);

  return (
    <form className='catalogForm'>
      <Box pt='20px'>
        <Grid columnGap='30px'>
          <GridItem xs={12}>
            <TextStyled appearance='title4' style={{ marginBottom: '10px' }}>
              Catalog Information
            </TextStyled>
          </GridItem>

          <GridItem xs={4}>
            <section>
              <Box mt='12px'>
                <StepBox>
                  Catalog Name &amp; Group <MandatoryFields color={nameError || groupError ? 'red' : 'black'} />
                </StepBox>

                <TextFieldStyled
                  id='catalogName'
                  autoComplete='off'
                  error={nameError}
                  errorMessage={errors.name}
                  label='Enter Name'
                  name='name'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />

                <TextFieldStyled
                  id='groupName'
                  autoComplete='off'
                  error={groupError}
                  errorMessage={errors.groupName}
                  label='Enter Group Name'
                  name='groupName'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.groupName}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
              </Box>
            </section>
            <section>
              <Box mt='12px'>
                <StepBox data-testid='sales_organization'>
                  Sales Organization <MandatoryFields color={salesOrgError ? 'red' : 'black'} />
                </StepBox>

                <DropDownStyled
                  id='salesOrg'
                  error={salesOrgError}
                  errorMessage={errors.salesOrg}
                  label='Select Sales Organization'
                  name='salesOrg'
                  onChange={handleChange}
                  options={SALES_ORGS}
                  value={values.salesOrg}
                />
              </Box>
            </section>

            <section>
              <Box mt='12px'>
                <StepBox>
                  Catalog Type <MandatoryFields color={catalogTypesError ? 'red' : 'black'} />
                </StepBox>
                <CatalogTypeField
                  associateCatalogType={associateCatalogType}
                  disassociateCatalogType={disassociateCatalogType}
                  parentType='catalog'
                />
              </Box>
            </section>
          </GridItem>

          <GridItem xs={4}>
            <section>
              <StepBox>
                Order Type(s) <MandatoryFields color={orderTypesError ? 'red' : 'black'} />
              </StepBox>

              <CheckboxSetStyled
                classes={{
                  Container: '',
                  Input: '',
                  Label: ''
                }}
                disabled={false}
                error={orderTypesError}
                errorMessage={errors.orderTypes}
                hasLargeLegend={false}
                inline='inline'
                onChange={handleOrderTypesChange}
                selectedValues={values.orderTypes}
                supportText='Note: either Futures or Forecast can be selected at a time, but not both'
              >
                {ORDER_TYPES.map(orderType => (
                  <CheckboxStyled
                    key={orderType.value}
                    id={orderType.value}
                    disabled={
                      (orderType.value === FUTURES && doesOrderTypesInclude(FORECAST)) ||
                      (orderType.value === FORECAST && doesOrderTypesInclude(FUTURES))
                    }
                    label={orderType.label}
                    value={orderType.value}
                  />
                ))}
              </CheckboxSetStyled>

              <div
                css={css`
                  animation: ${fadeIn} 0.5s ease;
                  display: ${doesOrderTypesInclude(ATONCE) ? 'block' : 'none'};
                `}
              >
                <TextStyled appearance='body1Strong' className='mb1-sm mt5-sm'>
                  At-Once Dates <MandatoryFields color={atOnceStartError || atOnceEndError ? 'red' : 'black'} />
                </TextStyled>

                <Box>
                  <DatePicker
                    id='atOnceStart'
                    error={atOnceStartError}
                    errorMessage={errors.atOnceStart}
                    label='At-Once Display Start'
                    name='atOnceStart'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.atOnceStart}
                    max={values.atOnceEnd || null}
                    {...(isEditing ? { min: null } : {})}
                  />

                  <DatePicker
                    id='atOnceEnd'
                    error={atOnceEndError}
                    errorMessage={errors.atOnceEnd}
                    label='At-Once Display End'
                    name='atOnceEnd'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.atOnceEnd}
                    min={values.atOnceStart || null}
                  />
                </Box>
              </div>

              <div
                css={css`
                  animation: ${fadeIn} 0.5s ease;
                  display: ${doesOrderTypesInclude(FUTURES) ? 'flex' : 'none'};
                  flex-direction: column;
                `}
              >
                <TextStyled appearance='title4' component='h5' className='mb1-sm mt5-sm'>
                  Futures Dates{' '}
                  <MandatoryFields color={futuresCrdError || futuresStartError || futuresEndError ? 'red' : 'black'} />
                </TextStyled>

                <DatePicker
                  id='futuresCrd'
                  error={futuresCrdError}
                  errorMessage={errors.futuresCrd}
                  label='Futures CRD'
                  name='futuresCrd'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.futuresCrd}
                  {...(isEditing ? { min: null } : {})}
                />

                <DatePicker
                  id='futuresStart'
                  error={futuresStartError}
                  errorMessage={errors.futuresStart}
                  label='Futures Display Start'
                  name='futuresStart'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.futuresStart}
                  max={values.futuresEnd || null}
                  {...(isEditing ? { min: null } : {})}
                />

                <DatePicker
                  id='futuresEnd'
                  error={futuresEndError}
                  errorMessage={errors.futuresEnd}
                  label='Futures Display End'
                  name='futuresEnd'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.futuresEnd}
                  min={values.futuresStart || null}
                />
              </div>

              <div
                css={css`
                  animation: ${fadeIn} 0.5s ease;
                  display: ${doesOrderTypesInclude(FORECAST) ? 'flex' : 'none'};
                  flex-direction: column;
                `}
              >
                <TextStyled appearance='title4' className='mb1-sm mt5-sm' component='h5'>
                  Forecast Dates{' '}
                  <MandatoryFields
                    color={forecastCrdError || forecastStartError || forecastEndError ? 'red' : 'black'}
                  />
                </TextStyled>

                <DatePicker
                  id='forecastCrd'
                  error={forecastCrdError}
                  errorMessage={errors.forecastCrd}
                  label='Forecast CRD'
                  name='forecastCrd'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.forecastCrd}
                  {...(isEditing ? { min: null } : {})}
                />

                <DatePicker
                  id='forecastStart'
                  error={forecastStartError}
                  errorMessage={errors.forecastStart}
                  label='Forecast Display Start'
                  name='forecastStart'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.forecastStart}
                  max={values.forecastEnd || null}
                  {...(isEditing ? { min: null } : {})}
                />

                <DatePicker
                  id='forecastEnd'
                  error={forecastEndError}
                  errorMessage={errors.forecastEnd}
                  label='Forecast Display End'
                  name='forecastEnd'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.forecastEnd}
                  min={values.forecastStart || null}
                />
              </div>
            </section>
          </GridItem>

          <GridItem xs={4}>
            <section>
              <StepBox>
                Season(s) <MandatoryFields color={seasonsError ? 'red' : 'black'} />
              </StepBox>

              <CheckboxStyled
                id='singleSeason'
                checked={values.singleSeason}
                className='mb4-sm'
                disabled={disableCheckbox}
                label='Single Season'
                onChange={handleSingleSeasonChange}
              />

              {values.singleSeason ? (
                <DropDownStyled
                  id='season'
                  error={seasonsError}
                  errorMessage={errors.seasons}
                  label='Season'
                  name='seasons'
                  onChange={handleSingleSeasonSelect}
                  options={seasonOptions}
                  value={values.seasons[0]}
                />
              ) : (
                <OptionRangeSelect
                  name='seasons'
                  error={seasonsError}
                  errorMessage={errors.seasons}
                  onChange={handleSeasonRangeSelect}
                  options={seasonOptions}
                  value={values.seasons}
                />
              )}
            </section>
            <section>
              <Box mt='12px'>
                <StepBox>Direct Ship</StepBox>

                <TextStyled appearance='body1Strong' className='mb5-sm'>
                  Is this catalog direct-ship?
                </TextStyled>

                <CheckboxStyled
                  id='directShip'
                  checked={values.directShip}
                  disabled={isDirectShip(values)}
                  label='Yes'
                  name='directShip'
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Box>
            </section>

            <section>
              <Box mt='12px' mb='28px'>
                <StepBox>Owner</StepBox>

                <DropDownStyled
                  id='ownerId'
                  error={touched.ownerId && errors.ownerId}
                  errorMessage={errors.ownerId}
                  label='Owner'
                  name='ownerId'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={sysAdmins}
                  value={values.ownerId}
                />
              </Box>
            </section>
          </GridItem>
        </Grid>

        <Flex flexDirection='row' justifyContent='flex-end' mt='10px' mr='20px'>
          {onCancel && (
            <ButtonStyled appearance='secondary' background='light' disabled={isSubmitting} onClick={handleCancel}>
              Cancel
            </ButtonStyled>
          )}
          <ButtonStyled
            appearance='primary'
            background='light'
            className='ml4-sm'
            disabled={isSubmitting}
            onClick={handleSubmit}
            size='small'
          >
            {isEditing ? 'Update' : 'Create'} Catalog
          </ButtonStyled>
        </Flex>
      </Box>
    </form>
  );
};

CatalogsCreateOrEditForm.propTypes = {
  fetchAdminUsers: func.isRequired,
  isEditing: bool.isRequired,
  onCancel: func.isRequired,
  seasons: arrayOf(seasonType).isRequired,
  userId: string.isRequired
};

const mapStateToProps = state => ({
  seasons: getSeasons(state),
  userId: getUserData(state)?.id
});

export default connect(mapStateToProps, { fetchAdminUsers })(CatalogsCreateOrEditForm);
